import axios from 'axios'
import {
  authHeadersForJohkuRequest,
  headersToPlainObject,
  isAuthStatusError,
  isJohkuBackendRequest,
  isValidApiKey,
  resolveJohkuRequestUrl
} from '~/lib/johkuApiAuth'
import {
  beginSessionRecovery,
  tryRefreshBetterAuthSession
} from '~/lib/sessionRecovery'

export default defineNuxtPlugin((nuxtApp) => {

  // GUARD: Only configure the global axios instance once
  if ((axios as any).__johkuConfigured) {
    return
  }
  (axios as any).__johkuConfigured = true

  const EventBus = nuxtApp.$eventbus as any
  const runtimeConfig = useRuntimeConfig()
  const baseDomain = runtimeConfig.public.baseDomain as string
  const serviceDomain = runtimeConfig.public.serviceDomain as string
  const store = nuxtApp.$store as {
    state: { user?: unknown, sessionNeedsRecovery?: boolean }
    getters?: { apiKey?: string | null }
    dispatch: (action: string, payload?: unknown) => unknown
  }

  /**
   * Encodes a single query param
   */
  function encodeAxiosParam(value: any) {
    let v = value;
    if (toString.call(value) === "[object Date]") {
      v = value.toISOString();
    } else if (typeof value === "object") {
      v = JSON.stringify(value);
    }
    return encodeURIComponent(v);
  }

  axios.defaults.paramsSerializer = {
    serialize: params => {
      return Object.entries(params)
        .filter(
          ([, value]) =>
            value !== undefined &&
            value !== null &&
            (Array.isArray(value) ? value.length > 0 : true)
        )
        .map(([key, value]) => {
          if (Array.isArray(value)) {
            return value.map((v) => `${key}[]=${encodeAxiosParam(v)}`).join('&')
          }
          return `${key}=${encodeAxiosParam(value)}`
        })
        .join('&')
    }
  }

  // Move apikey query params to Authorization header; inject store auth when missing.
  axios.interceptors.request.use(
    (config) => {
      // Handle URL-based apikey parameter
      if (config.url) {
        const url = new URL(config.url, 'https://example.com') // Use dummy base for relative URLs
        const apikey = url.searchParams.get('apikey')

        if (apikey) {
          // Remove apikey from URL
          url.searchParams.delete('apikey')

          // Update the config URL (preserve original if it was relative)
          if (config.url.startsWith('http')) {
            config.url = url.toString()
          } else {
            // For relative URLs, reconstruct without the protocol and host
            config.url = url.pathname + url.search + url.hash
          }

          if (isValidApiKey(apikey)) {
            config.headers = authHeadersForJohkuRequest(apikey, config.headers as Record<string, string> | Headers) as typeof config.headers
          }
        }
      }

      // Handle params-based apikey parameter
      if (config.params && config.params.apikey) {
        const apikey = config.params.apikey
        if (isValidApiKey(apikey)) {
          config.headers = authHeadersForJohkuRequest(apikey, config.headers as Record<string, string> | Headers) as typeof config.headers
        }
        delete config.params.apikey
      }

      const requestUrl = resolveJohkuRequestUrl(config.url || '', config.baseURL)
      if (isJohkuBackendRequest(requestUrl, baseDomain, serviceDomain)) {
        const apiKey = store?.getters?.apiKey
        config.headers = authHeadersForJohkuRequest(apiKey, config.headers as Record<string, string> | Headers) as typeof config.headers
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  axios.interceptors.response.use(
    (config) => {
      if (config && config.headers && config.headers['x-note']) {
        EventBus.$emit('showResponseNote', config.headers['x-note'])
      }
      return config
    },
    async (error) => {
      const config = error?.config
      if (!config || config.skipAuthRecovery || config._authRetried) {
        return Promise.reject(error)
      }
      if (!isAuthStatusError(error)) {
        return Promise.reject(error)
      }
      const requestUrl = resolveJohkuRequestUrl(config.url || '', config.baseURL)
      if (!isJohkuBackendRequest(requestUrl, baseDomain, serviceDomain)) {
        return Promise.reject(error)
      }
      const refreshed = await tryRefreshBetterAuthSession(store)
      if (refreshed.status === 'ok') {
        config._authRetried = true
        const headers = headersToPlainObject(config.headers as Record<string, string> | Headers)
        headers.Authorization = 'Bearer ' + refreshed.token
        config.headers = headers as typeof config.headers
        // Retry once. A second 401/403 is a real request error (permissions,
        // missing resource), not a dead session — do not open the overlay.
        return axios(config)
      }
      if (refreshed.status === 'expired') {
        beginSessionRecovery(store)
      }
      return Promise.reject(error)
    }
  )

})
